import { KeyNames } from "@packages/lib/utlils/filters/filterJson";
import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { GAConstants } from "./constants";
import { SCHOLARSHIP_PARAM_ORDER } from "@packages/lib/constants";
const keyName = KeyNames();
export function getQualCode(qualText: string) {
  switch (qualText) {
    case "degree-courses":
    case "Undergraduate Qualification":
      return "M";
    case "postgraduate-courses":
    case "postgraduate Qualification":
      return "L";
    case "foundation-degree-courses":
      return "A";
    case "access-foundation-courses":
    case "Access to Higher Education":
      return "T";
    case "hnd-hnc-courses":
      return "N";
    case "pgs":
      return "L";
    default:
      return "";
  }
}
export function getSearchPayload(
  searchParams: any,
  filterCookieParam: any,
  qualification: any,
  dynamicRandomNumber: any,
  userRegion: any,
) {
  const getParamArray = (param: string) => {
    const params = [searchParams, filterCookieParam];
    return params
      .map((source) => source?.[param]?.split(/[\s+]/))
      .flat()
      .filter(Boolean);
  };
  const subjectArray = getParamArray("subject");
  const courseArray = getParamArray("course");
  const locationArray = [searchParams, filterCookieParam]
    .map((param) => param?.location?.split(/[\s+]/))
    .flat()
    .filter(Boolean);
  const getScore = () => {
    const paramScore = getValueFromSources("score");
    return paramScore && !paramScore.includes(",")
      ? `0,${paramScore}`
      : paramScore;
  };
  const getValueFromSources = (key: string) => {
    const sources = [searchParams, filterCookieParam];
    for (const source of sources) {
      if (source?.[key]) {
        return source[key];
      }
    }
    return undefined;
  };
  const score = getScore();
  const searchPayload: any = {
    parentQualification: getQualCode(qualification),
    childQualification: getValueFromSources("qualification"),
    searchCategoryCode: "",
    searchSubject:
      process.env.PROJECT === "Whatuni" ? subjectArray : courseArray,
    searchKeyword: getValueFromSources(keyName?.keyword),
    jacsCode: getValueFromSources("jacs"),
    location: locationArray,
    studyMode:
      searchParams?.study_mode ??
      searchParams?.["study-mode"] ??
      filterCookieParam?.["study-mode"] ??
      filterCookieParam?.study_mode ??
      undefined,
    studyMethod:
      searchParams?.["study-method"] ??
      filterCookieParam?.["study-method"] ??
      searchParams?.study_method ??
      filterCookieParam?.study_method ??
      undefined,
    collegeId: "",
    collegeName: getValueFromSources("university"),
    pageNo: searchParams?.pageno ?? searchParams?.page_no ?? "1",
    locationType:
      getValueFromSources(keyName?.locationType) === undefined
        ? ""
        : [getValueFromSources(keyName?.locationType)],
    intakeYear: null,
    intakeMonth: getValueFromSources("month")?.toUpperCase()?.includes("ALL")
      ? ""
      : getValueFromSources("month")?.toUpperCase(),
    sortBy: getValueFromSources("sort")?.toUpperCase() || "",
    userCoordinates: "",
    distance:
      getValueFromSources("distance") === "any"
        ? ""
        : getValueFromSources("distance"),
    ucasTariffRange: score ?? undefined,
    userRegionId:
      typeof userRegion === "number" ? String(userRegion) : undefined,
    dynamicRandomNumber: dynamicRandomNumber,
    universityGroup:
      getValueFromSources(keyName?.russellGroup) === undefined
        ? ""
        : [getValueFromSources(keyName?.russellGroup)?.toUpperCase()],
    postCode: getValueFromSources("postcode"),
  };
  return searchPayload;
}

export const getOrdinalFor = (value: number): string => {
  const suffixes: Record<number, string> = {
    1: "st",
    2: "nd",
    3: "rd",
  };
  if (value % 100 >= 11 && value % 100 <= 13) {
    return "th";
  }
  return suffixes[value % 10] || "th";
};

export const capitalizeFirstLetter = (value: string): string => {
  if (!value || typeof value !== "string") return value;
  return value
    ?.split(" ")
    ?.map(
      (word: string) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    ?.join(" ");
};

export function toLowerInitial(str: string): string {
  if (str?.length === 0 || !str) {
    return str; // Return empty string if input is empty
  }
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export function isEmptyObject(object: Object) {
  return !(Object.keys(object).length > 0);
}

export const formatGBPAmount = (text: string): string => {
  if (!text?.includes("GBP")) return text;
  return text.replace(/(\d+)(?:\s*-\s*(\d+))?\s*GBP/g, (match, start, end) => {
    const formatNumber = (num: string) => `£${parseInt(num).toLocaleString()}`;
    return end
      ? `${formatNumber(start)} - ${formatNumber(end)}`
      : formatNumber(start);
  });
};

export const generateBreadcrumbSchema = (
  breadcrumbs: any[],
  baseUrl: string,
) => {
  const itemList = breadcrumbs.map((item, index) => {
    const isLast = index === breadcrumbs.length - 1;
    const url = item.url?.startsWith("http")
      ? item.url
      : `${baseUrl.replace(/\/$/, "")}${item.url || ""}`;

    const listItem: any = {
      "@type": "ListItem",
      position: index + 1,
      name: item.label || "Home",
    };

    if (!isLast && item.url) {
      listItem.item = url;
    }

    return listItem;
  });
  return {
    itemListElement: itemList,
  };
};
export async function getEncryptedEmail(email: any) {
  try {
    if (!email) return null;

    const encoder = new TextEncoder();
    const data = encoder.encode(email);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  } catch (error) {
    return null;
  }
}

export const trackMenuClick = (
  navTitle?: string,
  parentMenu?: string,
  navSubTitle?: string,
  eventName?: string,
) => {
  GA4DataLayer({
    event: GAConstants.track_event,
    event_name: eventName,
    page_name:
      typeof window !== "undefined"
        ? localStorage?.getItem("gaPageName") || "NA"
        : "NA",
    click_text: navTitle || "NA",
    navigation_category: parentMenu,
    navigation_subcategory: navSubTitle,
  });
};

const getNextPrevUrls = (
  urlPath: string | undefined,
  urlSearchParamsString: string,
  action: "next" | "prev",
  totalPages?: number,
): string => {
  if (!urlPath) return "";

  const pageKey = process.env.PROJECT === "Whatuni" ? "pageno" : "page_no";
  const params = new URLSearchParams(urlSearchParamsString || "");

  const currentPage = Number(params.get(pageKey) || 1);
  const targetPage = action === "next" ? currentPage + 1 : currentPage - 1;
  if (action === "prev" && targetPage < 1) {
    return "";
  }

  if (action === "next" && totalPages && targetPage > totalPages) {
    return "";
  }

  if (targetPage <= 1) {
    params.delete(pageKey);
  } else {
    params.set(pageKey, targetPage.toString());
  }

  const queryString = params.toString();
  return queryString ? `${urlPath}?${queryString}` : urlPath;
};

function validateScholarshipParamOrder(searchParams: URLSearchParams): boolean {
  const currentKeys = Array.from(searchParams.keys());
  const relevantKeys = currentKeys.filter((key) =>
    SCHOLARSHIP_PARAM_ORDER.includes(key),
  );

  // Get the order of relevant keys as they appear in SCHOLARSHIP_PARAM_ORDER
  const expectedOrder = SCHOLARSHIP_PARAM_ORDER.filter((key) =>
    relevantKeys.includes(key),
  );

  // Check if the relevant keys in the URL match the expected order
  let expectedIndex = 0;
  for (const key of currentKeys) {
    if (SCHOLARSHIP_PARAM_ORDER.includes(key)) {
      if (key !== expectedOrder[expectedIndex]) {
        return false;
      }
      expectedIndex++;
    }
  }

  return true;
}

function reorderScholarshipParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const orderedParams = new URLSearchParams();
  const currentKeys = Array.from(searchParams.keys());

  // First add params in the defined order
  SCHOLARSHIP_PARAM_ORDER.forEach((key) => {
    if (searchParams.has(key)) {
      searchParams
        .getAll(key)
        .forEach((value) => orderedParams.append(key, value));
    }
  });

  // Then add any other params that aren't in the order list
  currentKeys.forEach((key) => {
    if (!SCHOLARSHIP_PARAM_ORDER.includes(key)) {
      searchParams
        .getAll(key)
        .forEach((value) => orderedParams.append(key, value));
    }
  });

  return orderedParams;
}
export const orderUrlParams = (url: string, orderList: any[]): string => {
  const [base, query] = url?.split("?");
  if (!query) return url;

  const params = new URLSearchParams(query);
  const sorted = new URLSearchParams();

  orderList?.forEach((key: any) => {
    if (params?.has(key)) sorted?.set(key, params?.get(key)!);
  });

  Array.from(params?.keys())?.forEach((key) => {
    if (!orderList?.includes(key)) sorted.set(key, params?.get(key)!);
  });

  return `${base}?${sorted?.toString()}`;
};

function cleanUniversityUrl(url: string): string {
  const hasQueryParam = url.includes("?");
  const [base, query] = hasQueryParam ? url.split("?") : ["", url];
  if (!query) return url;

  const params = new URLSearchParams(query);
  const paramsToRemove = [
    keyName?.locationType,
    keyName?.location,
    keyName?.distance,
    keyName?.postcode,
    keyName?.score,
    keyName?.russellGroup,
  ];
  if (!params.has(keyName?.university)) return url;
  paramsToRemove.forEach((param) => params.delete(param));
  const cleanedQuery = params.toString();
  if (!hasQueryParam) return cleanedQuery;
  return cleanedQuery ? `${base}?${cleanedQuery}` : base;
}

export {
  getNextPrevUrls,
  cleanUniversityUrl,
  reorderScholarshipParams,
  validateScholarshipParamOrder,
};
